import { ref, computed } from "vue";
import { describeError } from "@/utils/error-message";

/*
 * The app's notification store, replacing `@dafcoe/vue-notification`.
 *
 * Why we stopped using the library rather than patching it:
 *
 *  1. It only ships CSS for four types — info, success, warning, alert. Eleven
 *     call sites passed `type: "error"`, which produced
 *     `.notification-list-item--error`, a class with ZERO rules in its
 *     stylesheet. Every failure in this app rendered as an untinted, unlabelled
 *     card indistinguishable from an FYI. Renaming "error" to "alert" would
 *     have fixed the colour and nothing else.
 *  2. It has no concept of an action, so a failure could never tell you what to
 *     do about it — which is the actual point of this work.
 *  3. It cannot dedupe. A bulk operation over 19 jobs stacked 19 cards over the
 *     table (see the comment above `announceVotes` in OrdersTable.vue).
 *  4. It auto-dismisses everything, including errors. A message you are meant
 *     to act on must not disappear while you are reading it.
 *
 * State lives at MODULE scope, not inside the composable, so every caller —
 * components, the Vuex store, axios interceptors, the global error handler in
 * main.js — shares the same queue and the single host in App.vue renders it.
 */

const SEVERITIES = ["error", "warning", "success", "info"];

/*
 * Errors are sticky (0 = never auto-dismiss). Everything else clears itself.
 * Warnings get the longest timed life because they usually carry a "you may
 * want to..." that is worth finishing reading.
 */
const DEFAULT_DURATION = {
  error: 0,
  warning: 9000,
  success: 4500,
  info: 6000,
};

/*
 * Beyond this the stack covers the page it is describing. Errors are never the
 * ones evicted — see `trim()`.
 */
const MAX_VISIBLE = 5;

const toasts = ref([]);

let nextId = 1;

/*
 * Timer bookkeeping is kept OUT of the reactive records: a `setTimeout` handle
 * is not view state, and mutating one inside a reactive object would wake every
 * watcher on the list once per pause/resume.
 */
const timers = new Map(); // id -> { handle, remaining, startedAt }

// Hovering or focusing the stack freezes ALL of it, not just the toast under
// the pointer. Reading the second card should not cost you the first one.
let isStackPaused = false;

const clearTimer = (id) => {
  const state = timers.get(id);
  if (state?.handle) clearTimeout(state.handle);
  timers.delete(id);
};

const resumeOne = (id) => {
  const state = timers.get(id);
  if (!state || state.handle) return;
  state.startedAt = Date.now();
  state.handle = setTimeout(() => dismiss(id), state.remaining);
};

const pauseOne = (id) => {
  const state = timers.get(id);
  if (!state || !state.handle) return;
  clearTimeout(state.handle);
  state.handle = null;
  state.remaining = Math.max(0, state.remaining - (Date.now() - state.startedAt));
};

const startTimer = (toast) => {
  clearTimer(toast.id);
  if (!toast.duration) return; // 0 / null = sticky
  timers.set(toast.id, { handle: null, remaining: toast.duration, startedAt: Date.now() });
  if (!isStackPaused) resumeOne(toast.id);
};

/**
 * Freeze every countdown. Called on pointer-enter and focus-in of the host.
 */
export const pauseAll = () => {
  isStackPaused = true;
  timers.forEach((_, id) => pauseOne(id));
};

/**
 * Resume every countdown from where it stopped.
 */
export const resumeAll = () => {
  isStackPaused = false;
  timers.forEach((_, id) => resumeOne(id));
};

export const dismiss = (id) => {
  clearTimer(id);
  const index = toasts.value.findIndex((toast) => toast.id === id);
  if (index !== -1) toasts.value.splice(index, 1);
};

export const dismissAll = () => {
  toasts.value.forEach((toast) => clearTimer(toast.id));
  toasts.value = [];
};

// Evict from the oldest end, but skip errors: an error is the one message the
// user still has to deal with, so a burst of successes must never push it off.
const trim = () => {
  while (toasts.value.length > MAX_VISIBLE) {
    const index = toasts.value.findIndex((toast) => toast.severity !== "error");
    dismiss(toasts.value[index === -1 ? 0 : index].id);
  }
};

const asText = (value) => (typeof value === "string" ? value.trim() : "");

const normalizeAction = (action) => {
  if (!action || typeof action.onClick !== "function") return null;
  const label = asText(action.label);
  if (!label) return null;
  return {
    label,
    onClick: action.onClick,
    // Most actions replace the message ("Retry" — if it fails again you get a
    // fresh toast). `keepOpen` is for actions that only navigate away.
    keepOpen: action.keepOpen === true,
  };
};

/**
 * Show a notification.
 *
 * @param {Object} options
 * @param {'error'|'warning'|'success'|'info'} [options.severity='info']
 * @param {string} options.title      short, bold, scannable — the whole message if that's all you have
 * @param {string} [options.detail]   one optional second line
 * @param {{label:string,onClick:Function,keepOpen?:boolean}} [options.action]
 * @param {number} [options.duration] ms; 0 = sticky. Defaults by severity.
 * @param {string} [options.dedupeKey] override the identity used for collapsing
 * @returns {number} the toast id
 */
export function notify(options = {}) {
  const severity = SEVERITIES.includes(options.severity) ? options.severity : "info";
  const title = asText(options.title) || asText(options.message);
  if (!title) return null; // nothing worth interrupting anyone for

  const detail = asText(options.detail);
  const action = normalizeAction(options.action);
  const duration = options.duration === undefined ? DEFAULT_DURATION[severity] : options.duration;

  /*
   * Identity for collapsing. Wording is the default key because that is what
   * the reader actually sees repeat — two failures with the same sentence are
   * the same news however different their causes were. Call sites that want
   * finer control (per job id, say) pass `dedupeKey` explicitly.
   */
  const dedupeKey = asText(options.dedupeKey) || `${severity}::${title}::${detail}`;

  const existing = toasts.value.find((toast) => toast.dedupeKey === dedupeKey);
  if (existing) {
    existing.count += 1;
    // The newest occurrence's action wins: for a Retry it closes over the most
    // recent arguments, and re-running the older closure could hit a stale row.
    if (action) existing.action = action;
    // Deliberately NOT moved to the end of the list. A repeating message that
    // jumped position on every repeat would be unreadable, and the count badge
    // already says it happened again.
    startTimer(existing);
    return existing.id;
  }

  const toast = {
    id: nextId++,
    severity,
    title,
    detail,
    action,
    duration,
    dedupeKey,
    count: 1,
  };

  // Appended, so existing toasts never shift under a pointer that is already
  // heading for their dismiss button. New ones grow the stack downward.
  toasts.value.push(toast);
  startTimer(toast);
  trim();

  return toast.id;
}

/* ── Convenience wrappers ───────────────────────────────────────────────── */

export const notifySuccess = (title, options = {}) =>
  notify({ ...options, severity: "success", title });

export const notifyInfo = (title, options = {}) =>
  notify({ ...options, severity: "info", title });

export const notifyWarning = (title, options = {}) =>
  notify({ ...options, severity: "warning", title });

export const notifyError = (title, options = {}) =>
  notify({ ...options, severity: "error", title });

/**
 * Show a failed request. This is the one every catch block should use.
 *
 * `describeError` supplies the severity, an actionable title, a detail line and
 * — for 401 and 409 — an action, all derived from the status code and whatever
 * the body happened to contain. Anything passed here overrides it, which is how
 * a call site adds the thing only it knows: how to retry.
 *
 *   notifyApiError(err, {
 *     fallback: "We couldn't pause this job.",
 *     action: { label: "Retry", onClick: () => performPause(client, key) }
 *   })
 *
 * @param {*} error the axios error
 * @param {Object} [options]
 * @param {string|{title:string,detail?:string}} [options.fallback] wording when the server gave us nothing
 * @returns {number} the toast id
 */
export function notifyApiError(error, options = {}) {
  const described = describeError(error, options.fallback);

  return notify({
    severity: options.severity || described.severity,
    title: options.title || described.title,
    detail: options.detail === undefined ? described.detail : options.detail,
    action: options.action || described.action,
    duration: options.duration,
    dedupeKey: options.dedupeKey,
  });
}

/**
 * Everything the host component and every call site needs.
 *
 * Shaped as a composable purely so call sites read the same as the old
 * `useNotificationStore()` they replace; the functions are module singletons
 * and are exported directly too, for non-component callers like the store.
 */
export function useToast() {
  return {
    toasts: computed(() => toasts.value),
    notify,
    notifySuccess,
    notifyInfo,
    notifyWarning,
    notifyError,
    notifyApiError,
    dismiss,
    dismissAll,
    pauseAll,
    resumeAll,
  };
}

export default useToast;
